import React, { useState, useEffect, useRef } from 'react';
import { FaSpinner, FaTimes, FaPaperPlane, FaSave, FaEye } from 'react-icons/fa';
import { API_ENDPOINTS } from '../../config/api';
import { useAdminAuth } from '../context/AdminAuthContext';

// Compose step shown before a quote email goes out. Recipients, subject and the
// message body are editable; the branded shell, quote summary and the tokenised
// "View Quote" button are assembled server-side and are deliberately not
// editable here, so a quote can never be sent with a broken access link.
const ITServicesQuoteEmailComposer = ({ quote, onClose, onSent }) => {
  const { token } = useAdminAuth();
  const quoteId = quote?.quoteId;

  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const [recipients, setRecipients] = useState('');
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [placeholders, setPlaceholders] = useState([]);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewing, setPreviewing] = useState(false);

  const getHeaders = () => ({
    'Content-Type': 'application/json',
    ...(token ? { Authorization: token } : {})
  });

  const post = async (payload) => {
    const response = await fetch(API_ENDPOINTS.IT_SERVICES_QUOTE_MANAGEMENT, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(payload)
    });
    return response.json();
  };

  // Load the prefilled draft (saved default template if one exists).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await post({ action: 'getQuoteEmailDraft', quoteId });
        if (cancelled) return;
        if (data.success && data.draft) {
          const d = data.draft;
          setRecipients((d.recipients || []).join(', '));
          setCc((d.cc || []).join(', '));
          setBcc((d.bcc || []).join(', '));
          setSubject(d.subject || '');
          setMessage(d.message || '');
          setPlaceholders(d.placeholders || []);
          setPreviewHtml(d.previewHtml || '');
        } else {
          setError(data.error || data.message || 'Failed to load email draft');
        }
      } catch (err) {
        if (!cancelled) setError('Failed to load email draft. Please try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [quoteId]);

  // Re-render the preview after edits settle, so the pane always reflects what
  // SES will actually send rather than a client-side approximation.
  const debounceRef = useRef(null);
  useEffect(() => {
    if (loading) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        setPreviewing(true);
        const data = await post({ action: 'previewQuoteEmail', quoteId, subject, message });
        if (data.success) setPreviewHtml(data.previewHtml || '');
      } catch (err) {
        // Preview is advisory — a failure here must not block sending.
        console.error('Preview failed:', err);
      } finally {
        setPreviewing(false);
      }
    }, 600);
    return () => clearTimeout(debounceRef.current);
  }, [subject, message, loading, quoteId]);

  const handleSaveTemplate = async () => {
    try {
      setSavingTemplate(true);
      setError(null);
      setNotice(null);
      const data = await post({ action: 'saveQuoteEmailTemplate', subject, message });
      if (data.success) {
        setNotice('Saved as the default for future quote emails.');
      } else {
        setError(data.error || 'Failed to save template');
      }
    } catch (err) {
      setError('Failed to save template. Please try again.');
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleSend = async () => {
    if (!recipients.trim()) {
      setError('At least one recipient is required');
      return;
    }
    try {
      setSending(true);
      setError(null);
      const data = await post({
        action: 'sendQuote',
        quoteId,
        recipients: recipients.split(',').map((s) => s.trim()).filter(Boolean),
        cc: cc.split(',').map((s) => s.trim()).filter(Boolean),
        bcc: bcc.split(',').map((s) => s.trim()).filter(Boolean),
        subject,
        message
      });
      if (data.success) {
        if (onSent) onSent(data);
      } else {
        setError(data.error || 'Failed to send quote');
      }
    } catch (err) {
      setError('Failed to send quote. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const inputClass =
    'w-full bg-[#3c3836] border border-[#504945] rounded-lg px-3 py-2 text-[#ebdbb2] ' +
    'placeholder-[#928374] focus:outline-none focus:border-[#FFD700]';

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-[#282828] rounded-xl w-full max-w-6xl max-h-[92vh] flex flex-col border border-[#504945]">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-[#504945]">
          <div>
            <h2 className="text-2xl font-bold text-[#ebdbb2]">Send Quote</h2>
            <p className="text-sm text-[#a89984] mt-1">
              {quote?.quoteNumber || quoteId} — review and edit before sending
            </p>
          </div>
          <button onClick={onClose} className="text-[#a89984] hover:text-[#ebdbb2] text-xl">
            <FaTimes />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20 text-[#ebdbb2]">
            <FaSpinner className="animate-spin mr-3" /> Loading draft…
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Editor */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-[#a89984] mb-1">To</label>
                <input
                  className={inputClass}
                  value={recipients}
                  onChange={(e) => setRecipients(e.target.value)}
                  placeholder="client@example.com, second@example.com"
                />
                <p className="text-xs text-[#928374] mt-1">Separate multiple addresses with commas.</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-[#a89984] mb-1">CC</label>
                  <input className={inputClass} value={cc} onChange={(e) => setCc(e.target.value)} placeholder="optional" />
                </div>
                <div>
                  <label className="block text-sm text-[#a89984] mb-1">BCC</label>
                  <input className={inputClass} value={bcc} onChange={(e) => setBcc(e.target.value)} placeholder="optional" />
                </div>
              </div>

              <div>
                <label className="block text-sm text-[#a89984] mb-1">Subject</label>
                <input className={inputClass} value={subject} onChange={(e) => setSubject(e.target.value)} />
              </div>

              <div>
                <label className="block text-sm text-[#a89984] mb-1">Message</label>
                <textarea
                  className={`${inputClass} font-mono text-sm`}
                  rows={12}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                />
                <p className="text-xs text-[#928374] mt-1">
                  Plain text — blank lines start a new paragraph. The Peaknet header, quote
                  summary and “View Quote” button are added automatically and can’t be edited.
                </p>
                {placeholders.length > 0 && (
                  <p className="text-xs text-[#928374] mt-1">
                    Placeholders: {placeholders.map((p) => `{{${p}}}`).join('  ')}
                  </p>
                )}
              </div>

              <button
                onClick={handleSaveTemplate}
                disabled={savingTemplate}
                className="text-sm bg-[#504945] text-[#ebdbb2] px-4 py-2 rounded-lg hover:bg-[#665c54] flex items-center space-x-2 disabled:opacity-50"
              >
                {savingTemplate ? <FaSpinner className="animate-spin" /> : <FaSave />}
                <span>Save as default for future quotes</span>
              </button>
            </div>

            {/* Preview */}
            <div className="flex flex-col">
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm text-[#a89984] flex items-center space-x-2">
                  <FaEye />
                  <span>Preview — exactly what the client receives</span>
                </label>
                {previewing && <FaSpinner className="animate-spin text-[#a89984]" />}
              </div>
              <iframe
                title="Quote email preview"
                srcDoc={previewHtml}
                className="w-full flex-1 min-h-[420px] bg-white rounded-lg border border-[#504945]"
                sandbox=""
              />
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="p-6 border-t border-[#504945] space-y-3">
          {error && (
            <div className="bg-red-500/20 border border-red-500/40 text-red-300 px-4 py-2 rounded-lg text-sm">
              {error}
            </div>
          )}
          {notice && (
            <div className="bg-green-500/20 border border-green-500/40 text-green-300 px-4 py-2 rounded-lg text-sm">
              {notice}
            </div>
          )}
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              disabled={sending}
              className="bg-[#504945] text-[#ebdbb2] px-6 py-2 rounded-lg font-semibold hover:bg-[#665c54] disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSend}
              disabled={sending || loading}
              className="bg-[#FFD700] text-[#282828] px-6 py-2 rounded-lg font-bold hover:bg-[#FFC700] flex items-center space-x-2 disabled:opacity-50"
            >
              {sending ? <FaSpinner className="animate-spin" /> : <FaPaperPlane />}
              <span>{sending ? 'Sending…' : 'Send Quote'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ITServicesQuoteEmailComposer;
